import { Check, Copy, TriangleAlert } from "lucide-react";
import { useState } from "react";
import type { IssuedWorker } from "@/lib/api";

/**
 * The token, once — for a Worker or a Recorder, which register the same way.
 *
 * Shown in a panel that has to be dismissed deliberately rather than as a line that
 * scrolls away: the Server keeps only a hash, so closing it is the last moment the token
 * exists anywhere but on the machine it is for.
 */
export function IssuedToken({
  issued,
  noun,
  onDismiss,
  children,
}: {
  issued: IssuedWorker;
  /** What to register again if the token is lost: "machine", "recorder". */
  noun: string;
  onDismiss: () => void;
  /** Where the token goes on the machine it is for. */
  children: React.ReactNode;
}) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(issued.token);
      setCopied(true);
    } catch {
      // A browser that refuses the clipboard still shows the token on screen, which is
      // the part that matters.
    }
  }

  return (
    <section className="rounded-[var(--radius-xl)] border border-[var(--warning)] bg-[var(--surface-glass)] p-[var(--space-6)] backdrop-blur-[var(--blur-sm)]">
      <div className="flex items-start gap-[var(--space-3)]">
        <TriangleAlert size={18} strokeWidth={2} className="mt-[2px] shrink-0 text-[var(--warning)]" />
        <div className="min-w-0">
          <h2 className="text-[length:var(--text-lg)] font-semibold text-[var(--text-primary)]">
            Copy this token now
          </h2>
          <p className="mt-1 text-[length:var(--text-sm)] text-[var(--text-secondary)]">
            {issued.worker.name} is registered. This is the only time the token is shown — the
            Server keeps a hash of it and cannot show it again. If you lose it, register the {noun}{" "}
            again.
          </p>
        </div>
      </div>

      <code className="mt-[var(--space-4)] block overflow-x-auto rounded-[var(--radius-lg)] bg-[var(--surface-1)] px-[var(--space-3)] py-[var(--space-2)] font-mono text-[length:var(--text-sm)] text-[var(--text-primary)]">
        {issued.token}
      </code>

      <p className="mt-[var(--space-3)] text-[length:var(--text-xs)] text-[var(--text-secondary)]">{children}</p>

      <div className="mt-[var(--space-4)] flex gap-[var(--space-2)]">
        <button
          type="button"
          onClick={() => void copy()}
          className="flex items-center gap-[var(--space-2)] rounded-[var(--radius-lg)] bg-[var(--primary)] px-[var(--space-4)] py-[var(--space-2)] text-[length:var(--text-sm)] font-semibold text-[var(--primary-foreground)]"
        >
          {copied ? <Check size={14} strokeWidth={2} /> : <Copy size={14} strokeWidth={2} />}
          {copied ? "Copied" : "Copy token"}
        </button>

        <button
          type="button"
          onClick={onDismiss}
          className="rounded-[var(--radius-lg)] border border-[var(--border-default)] px-[var(--space-4)] py-[var(--space-2)] text-[length:var(--text-sm)] font-semibold text-[var(--text-primary)]"
        >
          I have copied it
        </button>
      </div>
    </section>
  );
}
